// Authenticated change-password page (also used by first-login flow)
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ArrowRight, Shield } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError, useAdminAuth } from "@/lib/adminAuth";

export default function AdminChangePassword() {
    const navigate = useNavigate();
    const { refreshUser, user } = useAdminAuth();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [next2, setNext2] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (next.length < 8) return toast.error("New password must be at least 8 characters.");
        if (next !== next2) return toast.error("New passwords do not match.");
        setBusy(true);
        try {
            await adminApi.post("/admin/auth/change-password", { current_password: current, new_password: next });
            toast.success("Password updated.");
            await refreshUser();
            navigate("/admin", { replace: true });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0b1f22] text-ivory flex items-center justify-center px-6 py-12">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-gold">
                        <Shield size={12} /> Serenity Cove · {user?.name?.split(" ")[0]}
                    </div>
                    <h1 className="font-display text-4xl text-ivory mt-4">{user?.must_change_password ? "Set your password" : "Change your password"}</h1>
                    <p className="text-ivory/60 text-sm mt-3">{user?.must_change_password ? "Pick a strong personal password before continuing to the dashboard." : "Enter your current password and choose a new one."}</p>
                </div>
                <form onSubmit={submit} className="bg-white/[0.04] border border-white/10 backdrop-blur-md rounded-2xl p-8" data-testid="admin-change-password-form">
                    {[
                        { label: "Current password", val: current, set: setCurrent, key: "current" },
                        { label: "New password", val: next, set: setNext, key: "new" },
                        { label: "Confirm new password", val: next2, set: setNext2, key: "new2" },
                    ].map((f, i) => (
                        <label key={f.key} className={i === 0 ? "block" : "block mt-5"}>
                            <span className="text-[10px] uppercase tracking-[0.22em] text-ivory/60">{f.label}</span>
                            <div className="mt-2 flex items-center gap-3 border border-white/15 rounded-lg px-4 py-3 bg-black/20 focus-within:border-gold/50 transition-colors">
                                <Lock size={15} className="text-ivory/40" />
                                <input type="password" required minLength={i === 0 ? 1 : 8} value={f.val} onChange={(e) => f.set(e.target.value)}
                                    className="bg-transparent outline-none w-full text-ivory text-sm" placeholder="••••••••" />
                            </div>
                        </label>
                    ))}
                    <button type="submit" disabled={busy} className="mt-7 w-full bg-gold text-navy px-6 py-3.5 rounded-full text-[11px] uppercase tracking-[0.24em] font-bold hover:bg-gold/90 inline-flex items-center justify-center gap-2 disabled:opacity-60">
                        {busy ? "Saving…" : (<>Save <ArrowRight size={13} /></>)}
                    </button>
                </form>
            </div>
        </div>
    );
}
